import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Button,
  FormControl,
  Input,
  useToast,
  VStack,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  HStack
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { supabase } from '../../services/supabaseClient';
import AdminLayout from './AdminLayout';

const SorteioConfigPage = () => {
  const [loading, setLoading] = useState(false);
  const [premios, setPremios] = useState([]);
  const [editingPremio, setEditingPremio] = useState(null);
  const [novoPremio, setNovoPremio] = useState('');
  const toast = useToast();

  const fetchPremios = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('sorteio_premios')
      .select('*')
      .order('ordem', { ascending: true });

    if (!error) {
      setPremios(data);
    } else {
      toast({
        title: 'Erro ao carregar prêmios',
        description: error.message,
        status: 'error'
      });
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    fetchPremios();
  }, [fetchPremios]);

  const handleSave = async () => {
    if (!novoPremio.trim()) {
      toast({
        title: 'Descrição do prêmio é obrigatória',
        status: 'warning'
      });
      return;
    }

    setLoading(true);
    const ordem = premios.length + 1;

    const { error } = await supabase
      .from('sorteio_premios')
      .insert([{ descricao: novoPremio, ordem }]);

    setLoading(false);
    if (!error) {
      toast({
        title: 'Prêmio adicionado com sucesso!',
        status: 'success'
      });
      setNovoPremio('');
      fetchPremios();
    } else {
      toast({
        title: 'Erro ao adicionar prêmio',
        description: error.message,
        status: 'error'
      });
    }
  };

  const handleUpdate = async (id, descricao) => {
    setLoading(true);
    const { error } = await supabase
      .from('sorteio_premios')
      .update({ descricao })
      .eq('id', id);

    setLoading(false);
    if (!error) {
      toast({
        title: 'Prêmio atualizado com sucesso!',
        status: 'success'
      });
      setEditingPremio(null);
      fetchPremios();
    } else {
      toast({
        title: 'Erro ao atualizar prêmio',
        description: error.message,
        status: 'error'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este prêmio?')) return;

    setLoading(true);
    const { error } = await supabase
      .from('sorteio_premios')
      .delete()
      .eq('id', id);

    setLoading(false);
    if (!error) {
      toast({
        title: 'Prêmio excluído com sucesso!',
        status: 'success'
      });
      fetchPremios();
    } else {
      toast({
        title: 'Erro ao excluir prêmio',
        description: error.message,
        status: 'error'
      });
    }
  };

  return (
    <AdminLayout>
      <Box p={4} maxW="800px" mx="auto">
        <Heading size="lg" mb={6}>Prêmios do Sorteio</Heading>
        
        {loading ? (
          <Box textAlign="center" py={10}>
            <Spinner size="xl" />
          </Box>
        ) : (
          <VStack spacing={6} align="stretch">
            <HStack spacing={4}>
              <FormControl flex={1}>
                <Input
                  placeholder="Digite a descrição do prêmio"
                  value={novoPremio}
                  onChange={(e) => setNovoPremio(e.target.value)}
                />
              </FormControl>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={loading}
              >
                Adicionar Prêmio
              </Button>
            </HStack>

            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Ordem</Th>
                  <Th>Descrição</Th>
                  <Th width="100px">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {premios.map((premio) => (
                  <Tr key={premio.id}>
                    <Td>{premio.ordem}º</Td>
                    <Td>
                      {editingPremio === premio.id ? (
                        <Input
                          value={premio.descricao}
                          onChange={(e) => {
                            const newPremios = premios.map(p =>
                              p.id === premio.id
                                ? { ...p, descricao: e.target.value }
                                : p
                            );
                            setPremios(newPremios);
                          }}
                          onBlur={() => handleUpdate(premio.id, premio.descricao)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdate(premio.id, premio.descricao);
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        premio.descricao
                      )}
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<EditIcon />}
                          size="sm"
                          onClick={() => setEditingPremio(premio.id)}
                          aria-label="Editar"
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          onClick={() => handleDelete(premio.id)}
                          aria-label="Excluir"
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </VStack>
        )}
      </Box>
    </AdminLayout>
  );
};

export default SorteioConfigPage;
